// Modules
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PlatformModule } from '@angular/cdk/platform';
import { QuillModule } from 'ngx-quill';
import { MatTableModule } from '@angular/material/table';
import { MaterialModule } from './angular-material/material.module';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ColorPickerModule } from 'ngx-color-picker';
import { CdkTableModule } from '@angular/cdk/table';
import { TranslateModule } from '@ngx-translate/core';
import { TextMaskModule } from 'angular2-text-mask';
import { OrderModule } from 'ngx-order-pipe';
import { PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { MatChipsModule } from '@angular/material/chips';
import { EditorModule } from '@progress/kendo-angular-editor';
import { WindowModule } from '@progress/kendo-angular-dialog';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InputsModule, FormFieldModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { NgxMaskModule } from 'ngx-mask';

// Components
import { TylButtonComponent } from './controls/buttons/tyl-button.component';
import { ErrorPromptComponent } from './controls/error-prompt/error-prompt.component';
import { StudentHeaderComponent } from './student-header/student-header.component';
import { ExampleComponent } from './example/component-example/component-example.component';
import { ModalWindowExampleComponent } from './example/modal-window-example/modal-window-example.component';
import { ActionMenuAgGridComponent } from './controls/action-menu/action-menu-ag-grid.component';
import { ActionMenuComponent } from './controls/action-menu/action-menu.component';
import { StudentNameComponent } from './student/student-name/student-name.component';
import { StudentSearchComponent } from './student-search/student-search.component';
import { MassEmailComponent } from './controls/mass-email/mass-email.component';
import { SpinnerComponent } from './spinner/spinner.component';
import { ExpandCollapseButtonsComponent } from './controls/buttons/expand-collapse-buttons/expand-collapse-buttons.component';
import { TabsComponent } from './controls/tabs/tabs.component';
import { FilterComponent } from './controls/filter/filter.component';
import { TylHeaderButtonComponent } from './controls/buttons/tyl-header-button.component';
import { PageHeaderComponent } from './page-header/page-header.component';
import { SaveCancelButtonsComponent } from './controls/buttons/save-cancel-buttons/save-cancel-buttons.component';
import { AddAttachmentDialogComponent } from './add-attachment-dialog/add-attachment-dialog.component';
import { StudentAlertsComponent } from './student-alerts/student-alerts.component';
import { OnlineRegistrationService } from '../student360/online-registration/online-registration.service';
import { AlertsDialogComponent } from './student-alerts/alerts-dialog/alerts-dialog.component';
import { ProfileTableComponent } from './profile-table/profile-table.component';
import { DeleteProfileAttachmentDialogComponent } from './profile-table/delete-profile-attachment-dialog/delete-profile-attachment-dialog.component';
import { AddProfileAttachmentDialogComponent } from './profile-table/add-profile-attachment-dialog/add-profile-attachment-dialog.component';
import { AttachmentsMenuDialogComponent } from './profile-table/attachments-menu-dialog/attachments-menu-dialog.component';
import { AddEditContentDialogComponent } from './profile-table/add-edit-content-dialog/add-edit-content-dialog.component';
import { DeleteTopicDialogComponent } from './profile-table/delete-topic-dialog/delete-topic-dialog.component';
import { EditTopicDialogComponent } from './profile-table/edit-topic-dialog/edit-topic-dialog.component';
import { ProfileViewerDialogComponent } from './profile-viewer-dialog/profile-viewer-dialog.component';
import { AddTopicDialogComponent } from './profile-table/add-topic-dialog/add-topic-dialog.component';
import { AddFileComponent } from './controls/add-file/add-file.component';
import { AdditionalTeacherWarningDialogComponent } from './user-preferences/user-preferences-window/additional-teacher-warning-dialog/additional-teacher-warning-dialog.component';
import { CalculationPreferenceWarningDialogComponent } from './user-preferences/user-preferences-window/calculation-preference-warning-dialog/calculation-preference-warning-dialog.component';
import { CustomSortWarningDialogComponent } from './user-preferences/user-preferences-window/custom-sort-warning-dialog/custom-sort-warning-dialog.component';
import { NoGradingClassesDialogComponent } from './user-preferences/user-preferences-window/no-grading-classes-dialog/no-grading-classes-dialog.component';
import { PrimaryTeacherWarningDialogComponent } from './user-preferences/user-preferences-window/primary-teacher-warning-dialog/primary-teacher-warning-dialog.component';
import { SyncPreferencesDialogComponent } from './user-preferences/user-preferences-window/sync-preferences-dialog/sync-preferences-dialog.component';
import { FilterContentsComponent } from './controls/filter/filter-contents/filter-contents.component';
import { DarkTabsComponent } from './controls/tabs/dark-tabs.component';
import { AddLinkComponent } from '../classroom360/links/add-link/add-link.component';
import { ErrorPromptMessagesComponent } from './controls/error-prompt/error-prompt-messages.component';
import { TylDatepickerComponent } from './tyl-datepicker/tyl-datepicker.component';
import { TylMatMenuComponent } from './controls/menus/tyl-mat-menu.component';
import { TylHeaderMatMenuComponent } from './controls/menus/tyl-header-mat-menu.component';
import { TylFormFieldComponent } from './controls/tyl-form-field/tyl-form-field.component';
import { TylCheckboxComponent } from './controls/tyl-checkbox/tyl-checkbox.component';
import { TylRadioGroupComponent } from './controls/tyl-radio-group/tyl-radio-group.component';
import { TylCheckboxListComponent } from './controls/tyl-checkbox-list/tyl-checkbox-list.component';
import { FilterNotificationComponent } from './controls/filter/filter-notification/filter-notification.component';
import { AddEditReferralDialogComponent } from '../classroom360/discipline-referral/add-edit-referral-dialog/add-edit-referral-dialog.component';
import { NoteTableComponent } from '../classroom360/discipline-referral/note-table/note-table.component';
import { SelectCourseDialogComponent } from '../classroom360/guards/select-course/select-course-dialog/select-course-dialog.component';
import { AdvancedStudentSearchComponent } from './student-search/advanced-student-search/advanced-student-search.component';
import { DataQueryFilterTableComponent } from '../classroom360/data-query/data-query-filters/data-query-filter-table/data-query-filter-table.component';
import { DataQueryFieldLabelComponent } from '../classroom360/data-query/data-query-field-label/data-query-field-label.component';
import { DataQueryService } from '../classroom360/data-query/data-query.service';
import { DeleteRowDialogComponent } from '../classroom360/data-query/delete-row-dialog/delete-row-dialog.component';
import { InsertFieldDialogComponent } from '../classroom360/data-query/data-query-settings-for-export/insert-field-dialog/insert-field-dialog.component';
import { FieldSearchComponent } from '../classroom360/data-query/field-search/field-search.component';
import { DataQueryQueriesButtonComponent } from '../classroom360/data-query/data-query-queries-button/data-query-queries-button.component';
import { ViewQueriesDialogComponent } from '../classroom360/data-query/view-queries-dialog/view-queries-dialog.component';
import { AddEditQueryDialogComponent } from '../classroom360/data-query/add-edit-query-dialog/add-edit-query.component';
import { ErrorsExistDialogComponent } from '../classroom360/data-query/errors-exist-dialog/errors-exist-dialog.component';
import { DeleteQueryDialogComponent } from '../classroom360/data-query/delete-query-dialog/delete-query-dialog.component';
import { DataQueryUnsavedChangesDialogComponent } from '../classroom360/data-query/data-query-unsaved-changes-dialog/data-query-unsaved-changes-dialog.component';
import { ClassroomSummaryFilterComponent } from '../classroom360/classroom-summary/filter/classroom-summary-filter.component';
import { CustomPageComponent } from './custom-page/custom-page.component';
import { CustomFieldComponent } from './custom-page/custom-field/custom-field.component';
import { GeneralConfirmationComponent } from './controls/general-confirmation/general-confirmation.component';
import { FavoritesComponent } from './favorites/favorites.component';
import { FavoritesModalComponent } from './favorites/favorites-modal/favorites-modal.component';
import { TylGeneralMatMenuComponent } from './controls/menus/tyl-general-mat-menu.component';
import { TylMultipleSelectListComponent } from './controls/tyl-multiple-select-list/tyl-multiple-select-list.component';
import { TylSelectCheckAllComponent } from './controls/tyl-multiple-select-list/tyl-select-check-all.component';
import { GeneralSelectionComponent } from './controls/general-selection/general-selection.component';
import { CourseDescriptionDialogComponent } from './course-description-dialog/course-description-dialog.component';
import { ChangeEmailComponent } from './change-email/change-email.component';
import { ChangeEmailMessageComponent } from './change-email/change-email-message/change-email-message.component';
import { VirtualizedTableComponent } from './virtualized-table/virtualized-table.component';
import { StudentSelectionComponent } from './controls/student-selection/student-selection.component';
import { ImportErrorDialogComponent } from '../classroom360/data-query/add-edit-query-dialog/import-error-dialog/import-error-dialog.component';
import { FileTooLargeWarningDialogComponent } from './controls/add-file/file-too-large-warning-dialog/file-too-large-warning-dialog.component';
import { StudentInFocusComponent } from './student/student-in-focus/student-in-focus.component';
import { AddEditStudentListDialogComponent } from '../classroom360/student-lists/add-edit-student-list-dialog/add-edit-student-list-dialog.component';
import { EnterCsvDialogComponent } from './enter-csv-dialog/enter-csv-dialog.component';
import { PortalAccessDeniedComponent } from '../student360/portal-access-denied/portal-access-denied.component';
import { LayoutEditorComponent } from './custom-layouts/layout-editor/layout-editor.component';
import { StudentSearchWarningComponent } from './student-search/student-search-warning/student-search-warning.component';
import { BroadcastDialogComponent } from '../shared-modules/announcement/broadcast-dialog/broadcast-dialog.component';
import { AddNonStudentDialogComponent } from '../classroom360/discipline-referral/add-non-student-dialog/add-non-student-dialog.component';
import { StaffSearchComponent } from './staff-search/staff-search.component';
import { CourseSectionSearchComponent } from './course-section-search/course-section-search.component';
import { StudentInformationComponent } from './student-information/student-information.component';
import { StudentInfoPortalComponent } from './student-information/student-info-portal/student-info-portal.component';
import { ErrorDialogComponent } from './error-dialog/error-dialog.component';
import { NoRelationshipsWarningDialogComponent } from '../classroom360/data-query/no-relationships-warning-dialog/no-relationships-warning-dialog.component';
import { OverlayHeaderComponent } from './overlay-header/overlay-header.component';
import { TemplatesFromClassicDialogComponent } from '../classroom360/data-query/add-edit-query-dialog/templates-from-classic-dialog/templates-from-classic-dialog.component';
import { SavedLayoutsDialogComponent } from './custom-layouts/saved-layouts-dialog/saved-layouts-dialog.component';
import { AboutPanelComponent } from './controls/about-panel/about-panel.component';
import { ProjectScoresInfoComponent } from './user-preferences/user-preferences-window/project-scores-info/project-scores-info.component';
import { CustomLayoutComponent } from './custom-layouts/custom-layout/custom-layout.component';
import { SaveLayoutDialogComponent } from './custom-layouts/save-layout-dialog/save-layout-dialog.component';
import { EFormViewerComponent } from './e-forms/e-form-viewer/e-form-viewer.component';
import { EFormViewerColumnComponent } from './e-forms/e-form-viewer/e-form-viewer-column/e-form-viewer-column.component';
import { EFormViewerRowComponent } from './e-forms/e-form-viewer/e-form-viewer-row/e-form-viewer-row.component';
import { EFormViewerControlComponent } from './e-forms/e-form-viewer/e-form-viewer-control/e-form-viewer-control.component';
import { SelectedSitesDialogComponent } from './controls/selected-sites-dialog/selected-sites-dialog.component';
import { SelectedRolesDialogComponent } from './controls/selected-roles-dialog/selected-roles-dialog.component';
import { EFormNotesComponent } from './e-forms/e-form-notes/e-form-notes.component';
import { MassEditReferralDialogComponent } from './behavior/mass-edit-referral-dialog/mass-edit-referral-dialog.component';
import { StudentListModalComponent } from './student-search/student-list-modal/student-list-modal.component';
import { ProgramsAndServicesComponent } from '../student360/programs-and-services/programs-and-services.component';
import { AnnouncementMessageComponent } from '../shared-modules/announcement/announcement-message/announcement-message.component';
import { AnnouncementTableComponent } from '../shared-modules/announcement/announcement-table/announcement-table.component';
import { LayoutUnsavedChangesDialogComponent } from './custom-layouts/layout-editor/layout-unsaved-changes-dialog/layout-unsaved-changes-dialog.component';
import { CustomLayoutSectionComponent } from './custom-layouts/custom-layout/custom-layout-section/custom-layout-section.component';
import { CustomLayoutFieldComponent } from './custom-layouts/custom-layout/custom-layout-field/custom-layout-field.component';
import { CustomLayoutFieldLabelComponent } from './custom-layouts/custom-layout/custom-layout-field-label/custom-layout-field-label.component';
import { AddEditStudentRegCodeDialogComponent } from './add-edit-student-reg-code-dialog/add-edit-student-reg-code-dialog.component';
import { SaveWarningDialogComponent } from '../classroom360/discipline-referral/save-warning-dialog/save-warning-dialog.component';
import { IepComponent } from '../student360/iep/iep.component';
import { JobSentToQueueComponent } from './job-sent-to-queue/job-sent-to-queue.component';
import { AddNoteComponent } from '../classroom360/discipline-referral/add-note/add-note.component';
import { StaffListDialogComponent } from './staff-search/staff-list-dialog/staff-list-dialog.component';
import { AdvancedStaffSearchComponent } from './staff-search/advanced-staff-search/advanced-staff-search.component';
import { GroupConditionInfoDialogComponent } from '../classroom360/data-query/data-query-filters/group-condition-info-dialog/group-condition-info-dialog.component';
import { TylRTFComponent } from './controls/tyl-rtf/tyl-rtf.component';
import { ReEnrollStudentsComponent } from './student-header/re-enroll-students/re-enroll-students.component';
import { NoInternetConnectionModalComponent } from './no-internet-connection-modal/no-internet-connection-modal.component';
import { TylKendoPdfExportComponent } from './kendo/tyl-kendo-pdf-export/tyl-kendo-pdf-export.component';
import { AddEditCommunicationLogComponent } from '../shared-modules/communication-log/add-edit-communication-log/add-edit-communication-log.component';
import { EditTranslationsComponent } from './translation/edit-translations/edit-translations.component';
import { EditRichTextComponent } from './translation/edit-translations/edit-rich-text/edit-rich-text.component';
import { GPARankLegendComponent } from '../counselor360/gpa-class-rank/gpa-rank-legend/gpa-rank-legend.component';
import { LegendComponent } from './legend/legend.component';
import { StudentImageDialogComponent } from './student-header/student-image-dialog/student-image-dialog.component';
import { MultipleFamiliesComponent } from '../student360/online-registration/student-update/multiple-families/multiple-families.component';
import { AdminCommentDialogComponent } from './behavior/mass-edit-referral-dialog/admin-comment-dialog/admin-comment-dialog.component';
import { GradebookAccessDeniedComponent } from '../classroom360/gradebook/v9/modals/gradebook-access-denied/gradebook-access-denied.component';
import { TylCalWidgetComponent } from './tyl-cal-widget/tyl-cal-widget.component';
import { ProgressBarComponent } from './controls/progress-bar/progress-bar.component';
import { NoClassSelectedWarningDialogComponent } from '../classroom360/guards/select-course/no-class-selected-warning-dialog/no-class-selected-warning-dialog.component';
import { GradeOverrideLegendComponent } from './controls/grade-override-legend/grade-override-legend.component';
import { EFormDesignerComponent } from './e-forms/e-form-designer/e-form-designer.component';
import { EFormColumnComponent } from './e-forms/e-form-designer/e-form-column/e-form-column.component';
import { EFormRowComponent } from './e-forms/e-form-designer/e-form-row/e-form-row.component';
import { EFormControlComponent } from './e-forms/e-form-designer/e-form-control/e-form-control.component';
import { EFormSidePanelComponent } from './e-forms/e-form-designer/e-form-side-panel/e-form-side-panel.component';
import { EFormNavigationPanelComponent } from './e-forms/e-form-designer/e-form-side-panel/e-form-navigation-panel/e-form-navigation-panel.component';
import { EFormPropertiesPanelComponent } from './e-forms/e-form-designer/e-form-side-panel/e-form-properties-panel/e-form-properties-panel.component';
import { EFormAvailableControlsComponent } from './e-forms/e-form-designer/e-form-side-panel/e-form-available-controls/e-form-available-controls.component';
import { MergeFieldDialogComponent } from './e-forms/e-form-designer/merge-field-dialog/merge-field-dialog.component';
import { EFormChoicesDialogComponent } from './e-forms/e-form-designer/e-form-choices-dialog/e-form-choices-dialog.component';
import { ValueFromAnotherFieldDialogComponent } from './e-forms/e-form-designer/value-from-another-field-dialog/value-from-another-field-dialog.component';
import { EFormTemplateInUseWarningComponent } from './e-forms/e-form-designer/e-form-template-in-use-warning/e-form-template-in-use-warning.component';
import { FloatingGridComponent } from './floating-grid/floating-grid.component';
import { StudentDocumentAccessHistoryComponent } from '../student360/student-documents/student-document-access-history/student-document-access-history.component';
import { QuillTextOnlyComponent } from './quill-text-only/quill-text-only.component';
import { QuillTextOnlyOneLineComponent } from './quill-text-only/quill-text-only-one-line/quill-text-only-one-line.component';
import { SelectorComponent } from './controls/selector/selector.component';
import { TylRaceEthnicityComponent } from './controls/tyl-race-ethnicity/tyl-race-ethnicity.component';
import { TylRaceEthnicityListComponent } from './controls/tyl-race-ethnicity/tyl-race-ethnicity-list.component';
import { TylTimeFieldComponent } from './controls/tyl-time-field/tyl-time-field.component';
import { TylDragSelectionComponent } from './controls/tyl-drag-selection/tyl-drag-selection.component';
import { MassEmailViewComponent } from '../shared-components/mass-email-view/mass-email-view.component';
import { SelectRecipientsComponent } from '../shared-components/mass-email-view/select-recipients/select-recipients.component';
import { UnselectedWarningComponent } from '../shared-components/mass-email-view/unselected-warning/unselected-warning.component';
import { NoEmailWarningComponent } from '../shared-components/mass-email-view/no-email-warning/no-email-warning.component';
import { MassEmailAttachmentComponent } from '../shared-components/mass-email-view/mass-email-attachment/mass-email-attachment.component';
import { SendEmailsWarningComponent } from '../shared-components/mass-email-view/send-emails-warning/send-emails-warning.component';
import { ByClassRowStudentComponent } from '../shared-components/mass-email-view/select-recipients/by-class-row-student/by-class-row-student.component';
import { ViewRecipientsModalComponent } from '../shared-components/mass-email-view/view-recipients-modal/view-recipients-modal.component';
import { SendTestEmailComponent } from '../shared-components/mass-email-view/send-test-email/send-test-email.component';
import { ByTeamRowStudentComponent } from '../shared-components/mass-email-view/select-recipients/by-team-row-student/by-team-row-student.component';
import { ByListRowStudentComponent } from '../shared-components/mass-email-view/select-recipients/by-list-row-student/by-list-row-student.component';
import { EFormViewerHeaderComponent } from './e-forms/e-form-viewer/e-form-viewer-header/e-form-viewer-header.component';
import { MassEditDialogComponent } from './mass-edit-dialog/mass-edit-dialog.component';
import { ValueToUpdateComponent } from './mass-edit-dialog/value-to-update/value-to-update.component';
import { TylToggleChipComponent } from './controls/tyl-toggle-chip/tyl-toggle-chip.component';
import { PortalMenuComponent } from './portal-menu/portal-menu.component';
import { ApplicationMenuComponent } from './portal-menu/application-menu/application-menu.component';
import { FavoriteMenuComponent } from './portal-menu/favorite-menu/favorite-menu.component';
import { StudentInFocusMenuComponent } from './portal-menu/student-in-focus-menu/student-in-focus-menu.component';

// Services
import { LookupService } from './lookup/lookup.service';
import { StudentSearchService } from './student-search/student-search.service';
import { ProfileService } from './profile-table/profile.service';
import { FamilyRegistrationService } from '../shared-modules/family-registration/family-registration.service';
import { DisciplineReferralService } from '../classroom360/discipline-referral/discipline-referral.service';
import { SpinnerService } from './spinner/spinner.service';
import { CustomPageService } from './custom-page/custom-page.service';
import { SendEmailService } from '../shared-modules/send-email/send-email.service';
import { CustomLayoutsService } from './custom-layouts/custom-layouts.service';
import { StudentListsService } from '../classroom360/student-lists/student-lists.service';
import { AnnouncementService } from '../shared-modules/announcement/announcement.service';
import { BehaviorSharedService } from './behavior/behavior-shared.service';
import { EFormService } from './e-forms/e-form.service';
import { ProgramsAndServicesService } from '../student360/programs-and-services/programs-and-services.service';
import { StaffSearchService } from './staff-search/staff-search.service';
import { TranslationService } from './translation/translation.service';
import { BehaviorService } from '../counselor360/behavior/behavior.service';
import { CourseSectionSearchService } from './course-section-search/course-section-search.service';
import { EFormDesignerService } from './e-forms/e-form-designer/e-form-designer.service';
import { StaffCardsService } from '../classroom360/staff-home/staff-cards/staff-cards.service';
import { NotificationsService } from '../admin360/notifications/notifications.service';
import { CourseRequestMgmtService } from '../counselor360/scheduling/course-request-mgmt/course-request-mgmt.service';
import { ScheduleFolderService } from '../counselor360/scheduling/schedule-folder/schedule-folder.service';
import { MassEmailService } from '../shared-components/mass-email-view/mass-email.service';

// Directives
import { Draggable } from './directives/draggable.directive';
import { AriaDirective } from './directives/aria.directive';
import { CdkDetailRowDirective } from './directives/cdk-detail-row.directive';
import { RegexDirective } from './directives/regex.directive';
import { NumbersOnlyDirective } from './directives/numbers-only.directive';
import { DateCharactersOnlyDirective } from './directives/dateCharactersOnly.directive';
import { DateValidator } from './tyl-datepicker/date.validator';
import { ScrollToRowDirective } from './angular-material/scroll-to-row.directive';
import { DynamicComponentDirective } from './directives/dynamic-component.directive';
import { PhoneFormatDirectiveValidator } from './validators/phone-format-validator.directive';
import { EmailFormatDirectiveValidator } from './validators/email-format-validator.directive';
import { EllipsisDirective } from './directives/ellipsis.directive';
import { DecimalFormatValidatorDirective } from './directives/decimal-format-validator.directive';
import { StudentInformationDirective } from './student-information/student-information.directive';
import { FalseyRequiredDirectiveValidator } from './validators/falsey-required-validator.directive';
import { DebounceDirective } from './directives/debounce.directive';
import { MinimumDirective } from './directives/minimum.directive';
import { MaximumDirective } from './directives/maximum.directive';
import { ExternalLinkDirective } from './directives/external-link.directive';
import { NumberMaskDirective } from './directives/number-mask.directive';

// Pipes
import { SafeHtml } from './pipes/safe-html.pipe';
import {
    ChildPortalFilterPipe,
    PortalFilterPipe,
    TileFilterPipe,
    SchoolLayoutFilterPipe,
    SchoolLayoutAllFilterPipe
} from './user-preferences/user-preferences-window/user-preferences-window.component';
import { MenuFilterByGroupPipe } from './pipes/menu-filter-by-group.pipe';
import { LookUpFilterPipe, LookUpDescription } from '../shared-modules/communication-log/communication-log.component';
import { YesNoPipe } from './pipes/yes-no.pipe';
import { FilterClass } from '../classroom360/classroom-summary/classFilter.pipe';
import { EllipsisPipe } from './pipes/ellipsis.pipe';
import { GetFieldDisplayNamePipe } from './custom-layouts/pipes/get-field-display-name.pipe';
import { GetDataTypePipe } from './custom-layouts/pipes/get-data-type.pipe';
import { GetSpecialFieldSectionIdPipe } from './custom-layouts/pipes/get-special-field-section-id.pipe';
import { SafeResourceUrlPipe } from './pipes/safe-resource-url.pipe';
import { CommaDelimitedPipe } from './pipes/comma-delimited.pipe';
import { MergeFieldPipe } from './e-forms/merge-field.pipe';
import { SanitizePipe } from './pipes/sanitize.pipe';
import { LastFirstNamePipe } from './pipes/lastFirstName.pipe';
import { DateIsTodayPipe } from './pipes/date-is-today.pipe';
import { TylTranslatePipe } from './pipes/tyl-translate.pipe';
import { TranslationsStatusComponent } from './translation/translations-status/translations-status.component';
import { MinimumEnforceDirective } from './directives/minimum-enforce.directive';
import { MaximumEnforceDirective } from './directives/maximum-enforce.directive';
import { PhoneNumberPipe } from './pipes/phone-number.pipe';
import { MaxPrecisionDirective } from './directives/max-precision.directive';
import { GetDesignerControlNamePipe } from './pipes/get-designer-control-name.pipe';
import { FormatEFormControlTypePipe } from './pipes/format-eform-control-type.pipe';
import { GetControlImagePipe } from './pipes/get-control-image.pipe';
import { EFormViewerModalComponent } from './e-forms/e-form-viewer/e-form-viewer-modal/e-form-viewer-modal.component';
import { OverrideCreditcodeComponent } from '../student360/graduation-plan/override-creditcode/override-creditcode.component';
import { WarningDialogComponent } from '../classroom360/data-query/warning-dialog/warning-dialog.component';
import { EFormPdfNameComponent } from '../admin360/e-forms/e-form-submissions/e-Form-PDF-Name/e-form-pdf-name.component';
import { IncidentTypePipe } from './pipes/incident-type.pipe';
import { GradebookPreferencesResetWarningsComponent } from './user-preferences/user-preferences-window/gradebook-preferences-reset-warnings/gradebook-preferences-reset-warnings.component';
import { EFormRrowColumnLocationComponent } from './e-forms/e-form-designer/e-form-row-column-location/e-form-row-column-location';
import { EFormSelectedStudentsComponent } from '../admin360/e-forms/e-form-instances/e-form-selected-students/e-form-selected-students.component';
import { CustomizationFrameworkService } from '../shared-modules/customization-framework/customization-framework.service';
import { CustomDataFieldComponent } from '../shared-modules/customization-framework/custom-data-field/custom-data-field.component';
import { CustomizationService } from '../admin360/customization/customization.service';
import { SharedGradebookService } from '../classroom360/gradebook/shared/shared-gradebook.service';
import { GetSurveyAnswerByFieldIDPipe } from './e-forms/survey-answers-popup/get-survey-answer-by-field-id.pipe';
import { SurveyAnswersPopupComponent } from './e-forms/survey-answers-popup/survey-answers-popup.component';
import { SurveyAnswerIsCriticalPipe } from './e-forms/survey-answers-popup/survey-answer-is-critical.pipe';
import { PreferencesButtonComponent } from './preferences-button/preferences-button.component';
import { SurveyNotCompletedPopupComponent } from './e-forms/survey-not-completed-popup/survey-not-completed-popup.component';
import { EFormSurveyAnswerSummaryComponent } from './e-forms/e-form-survey-answer-summary/e-form-survey-answer-summary.component';
import { CalculatePercentPipe } from './pipes/calculate-percent.pipe';
import { ProgramAndServiceFieldsComponent } from './program-and-service-fields/program-and-service-fields.component';
import { NotificationsTableComponent } from './notifications-table/notifications-table.component';
import { NotificationDetailsDialogComponent } from './notifications-table/notification-details-dialog/notification-details-dialog.component';
import { StudentsToAddDialogComponent } from './students-to-add-dialog/students-to-add-dialog.component';
import { StudentNotesSharedComponent } from './student-notes-shared/student-notes-shared.component';
import { StudentNotesComponent } from '../classroom360/student-notes/student-notes.component';
import { StudentNotesService } from '../classroom360/student-notes/student-notes.service';
import { StudentInfoDirective } from '../admin360/student-info/student-info.directive';
import { SortByPipe } from './pipes/sort-by.pipe';
import { BehaviorLetterViewerModalComponent } from '../counselor360/behavior/behavior-letters/behavior-letter-viewer-modal/behavior-letter-viewer-modal.component';
import { EFormSurveyAnswerDetailsComponent } from './e-forms/e-form-survey-answer-details/e-form-survey-answer-details.component';
import { EFormSurveyAnswersFilterComponent } from './e-forms/e-form-survey-answer-details/e-form-survey-answers-filter/e-form-survey-answers-filter.component';
import { HouseholdFamilyDocumentComponent } from '../admin360/registration/detailed-registration/household-registration/household-family-document/household-family-document.component';
import { AddFamilyDocumentDialogComponent } from '../admin360/registration/detailed-registration/household-registration/household-family-document/add-family-document-dialog/add-family-document-dialog.component';
import { SelectStudentPipe } from '../admin360/registration/detailed-registration/household-registration/household-family-document/select-student.pipe';
import { RegistrationEnrollmentService } from '../admin360/registration/registration-enrollment.service';
import { SingleStudentComponent } from '../management360/program-and-service/single-student/single-student.component';
import { AddEditProgramServiceSingleStudentComponent } from '../management360/program-and-service/single-student/add-edit-program-service-single-student/add-edit-program-service-single-student.component';
import { InvalidSelectionDialogComponent } from '../management360/program-and-service/single-student/invalid-selection-dialog/invalid-selection-dialog.component';
import { DeleteProgramServiceDialogComponent } from '../management360/program-and-service/delete-program-service-dialog/delete-program-service-dialog.component';
import { CopyButtonComponent } from './controls/copy-and-paste-buttons/copy-button/copy-button.component';
import { PasteButtonComponent } from './controls/copy-and-paste-buttons/paste-button/paste-button.component';
import { AttendanceCheckInOutLegendComponent } from './attendance-check-in-out-legend/attendance-check-in-out-legend.component';
import { SelfCheckInOutHistoryDialogComponent } from './self-check-in-out-history-dialog/self-check-in-out-history-dialog.component';
import { StaffHeaderComponent } from './staff-header/staff-header.component';
import { AssignedSchoolsComponent } from './staff-header/modals/assigned-schools/assigned-schools.component';
import { StaffAlertComponent } from './staff-header/modals/staff-alert/staff-alert.component';
import { StudentHealthAlertComponent } from './student-health-alert/student-health-alert.component';
import { ViewAttachmentsComponent } from './view-attachments/view-attachments.component';
import { ViewAbsentNoticeHistoryDialogComponent } from './view-absent-notice-history-dialog/view-absent-notice-history-dialog.component';
import { CheckInOutViewHistoryDialogComponent } from './check-in-out-view-history-dialog/check-in-out-view-history-dialog.component';
import { YearDescriptionPipe } from './pipes/year-description.pipe';
import { HighlightSearchPipe } from './pipes/highlight-search.pipe';
import { SelectRecipientsPipe } from '../shared-components/mass-email-view/select-recipients/select-recipients.pipe';
import { ReplacePipe } from './pipes/replace.pipe';
import { EFormHeaderComponent } from './e-forms/e-form-designer/e-form-header/e-form-header.component';
import { CmpComponentDirective } from './directives/cmp-component.directive';
import { DataDefDropdownComponent } from './controls/data-def-control/data-def-dropdown/data-def-dropdown.component';
import { DataDefButtonComponent } from './controls/data-def-control/data-def-button/data-def-button.component';
import { DataDefinitionComponent } from '../admin360/data-definition/data-definition.component';
import { DataDefinitionStaticComponent } from '../admin360/data-definition/data-definition-static/data-definition-static.component';
import { DataDefinitionDynamicComponent } from '../admin360/data-definition/data-definition-dynamic/data-definition-dynamic.component';
import { AddEditDataDefinitionComponent } from '../admin360/data-definition/data-definition-static/add-edit-data-definition/add-edit-data-definition.component';
import { AddEditDynamicDataDefinitionComponent } from '../admin360/data-definition/data-definition-dynamic/add-edit-dynamic-data-definition/add-edit-dynamic-data-definition.component';
import { TylKendoHtmlEditorComponent } from './kendo/tyl-kendo-html-editor/tyl-kendo-html-editor.component';
import { DataDefRadioButtonsComponent } from './controls/data-def-control/data-def-radio-buttons/data-def-radio-buttons.component';
import { DataDefButtonListComponent } from './controls/data-def-control/data-def-button-list/data-def-button-list.component';
import { AssessmentComponent } from '../admin360/student-info/assessment/assessment.component';
import { AssessmentDataCategoryTableComponent } from '../admin360/student-info/assessment/assessment-data-category-table/assessment-data-category-table.component';
import { AssessmentDataTableComponent } from '../admin360/student-info/assessment/assessment-data-table/assessment-data-table.component';
import { AddAssessmentScoresComponent } from '../admin360/student-info/assessment/assessment-data-category-table/add-assessment-scores/add-assessment-scores.component';
import { AddAssessmentTabDialogComponent } from '../admin360/student-info/assessment/add-assessment-tab-dialog/add-assessment-tab-dialog.component';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { EFormFormatWarningComponent } from './e-forms/e-form-designer/e-form-format-warning/e-form-format-warning.component';
import { RequireStudentGuard } from './guards/require-student/require-student.guard';
import { RequireStaffGuard } from './guards/require-staff/require-staff.guard';
import { EFormConfirmSubmitComponent } from './e-forms/e-form-confirm-submit/e-form-confirm-submit.component';
import { AddEditNoteDialogComponent } from '../classroom360/student-notes/add-edit-note-dialog/add-edit-note-dialog.component';
import { ReportingFrameworkService } from '../classroom360/reporting-framework/reporting-framework.service';
import { DataDefMultiSelectComponent } from './controls/data-def-control/data-def-multi-select/data-def-multi-select.component';
import { BehaviorLetterTemplateSettingsComponent } from './behavior-letter-template-settings/behavior-letter-template-settings.component';
import { MyReportsComponent } from '../classroom360/reporting-framework/my-reports/my-reports.component';
import { MyReportsTabComponent } from '../classroom360/reporting-framework/my-reports/my-reports-tab/my-reports-tab.component';
import { AssignedReportsTabComponent } from '../classroom360/reporting-framework/my-reports/assigned-reports-tab/assigned-reports-tab.component';
import { AddMyReportFolderDialogComponent } from '../classroom360/reporting-framework/my-reports/add-my-report-folder-dialog/add-my-report-folder-dialog.component';
import { FoldersThisReportDialogComponent } from '../classroom360/reporting-framework/my-reports/folders-this-report-dialog/folders-this-report-dialog.component';
import { StaffImageDialogComponent } from './staff-header/modals/staff-image-dialog/staff-image-dialog.component';
import { AttachmentTableComponent } from './profile-table/attachment-table/attachment-table.component';
import { MonthDayPickerComponent } from './controls/month-day-picker/month-day-picker.component';
import { CourseSearchOptionComponent } from './course-search-option/course-search-option.component';
import { ColorLegendDialogComponent } from './color-legend-dialog/color-legend-dialog.component';

// TODO break out some of these components into their own modules to avoid circular dependencies.
@NgModule({
    declarations: [
        TylButtonComponent,
        TylHeaderButtonComponent,
        ChangeEmailComponent,
        ChangeEmailMessageComponent,
        ErrorPromptComponent,
        StudentHeaderComponent,
        StaffHeaderComponent,
        ExampleComponent,
        ModalWindowExampleComponent,
        ActionMenuAgGridComponent,
        ActionMenuComponent,
        SaveCancelButtonsComponent,
        Draggable,
        CdkDetailRowDirective,
        MassEmailComponent,
        SpinnerComponent,
        ExpandCollapseButtonsComponent,
        TabsComponent,
        StudentSelectionComponent,
        GeneralSelectionComponent,
        FilterComponent,
        FilterContentsComponent,
        SafeHtml,
        AriaDirective,
        RegexDirective,
        EllipsisDirective,
        StudentNameComponent,
        StudentSearchComponent,
        FavoritesComponent,
        AddLinkComponent,
        PageHeaderComponent,
        AddAttachmentDialogComponent,
        StudentAlertsComponent,
        AlertsDialogComponent,
        AddFileComponent,
        ProfileTableComponent,
        EditTopicDialogComponent,
        DeleteTopicDialogComponent,
        AddEditContentDialogComponent,
        AttachmentsMenuDialogComponent,
        AddProfileAttachmentDialogComponent,
        DeleteProfileAttachmentDialogComponent,
        AddTopicDialogComponent,
        ProfileViewerDialogComponent,
        ChildPortalFilterPipe,
        SchoolLayoutFilterPipe,
        SchoolLayoutAllFilterPipe,
        PortalFilterPipe,
        TileFilterPipe,
        EllipsisPipe,
        AdditionalTeacherWarningDialogComponent,
        CalculationPreferenceWarningDialogComponent,
        CustomSortWarningDialogComponent,
        NoGradingClassesDialogComponent,
        PrimaryTeacherWarningDialogComponent,
        SyncPreferencesDialogComponent,
        CourseDescriptionDialogComponent,
        DarkTabsComponent,
        NumbersOnlyDirective,
        DateCharactersOnlyDirective,
        TylDatepickerComponent,
        ErrorPromptMessagesComponent,
        TylMatMenuComponent,
        TylHeaderMatMenuComponent,
        TylFormFieldComponent,
        FavoritesComponent,
        FavoritesModalComponent,
        TylGeneralMatMenuComponent,
        TylCheckboxComponent,
        TylRadioGroupComponent,
        TylCheckboxListComponent,
        TylMultipleSelectListComponent,
        TylSelectCheckAllComponent,
        MenuFilterByGroupPipe,
        DateValidator,
        ScrollToRowDirective,
        VirtualizedTableComponent,
        FilterNotificationComponent,
        LookUpFilterPipe,
        LookUpDescription,
        SelectedSitesDialogComponent,
        GeneralConfirmationComponent,
        DynamicComponentDirective,
        AddEditReferralDialogComponent,
        AddNoteComponent,
        NoteTableComponent,
        CustomPageComponent,
        CustomFieldComponent,
        YesNoPipe,
        SelectCourseDialogComponent,
        NoClassSelectedWarningDialogComponent,
        ClassroomSummaryFilterComponent,
        FilterClass,
        AdvancedStudentSearchComponent,
        DataQueryFilterTableComponent,
        DataQueryFieldLabelComponent,
        DeleteRowDialogComponent,
        InsertFieldDialogComponent,
        FieldSearchComponent,
        DataQueryQueriesButtonComponent,
        ViewQueriesDialogComponent,
        AddEditQueryDialogComponent,
        ErrorsExistDialogComponent,
        DeleteQueryDialogComponent,
        DataQueryUnsavedChangesDialogComponent,
        ImportErrorDialogComponent,
        ErrorDialogComponent,
        NoRelationshipsWarningDialogComponent,
        PhoneFormatDirectiveValidator,
        EmailFormatDirectiveValidator,
        FalseyRequiredDirectiveValidator,
        OverlayHeaderComponent,
        FileTooLargeWarningDialogComponent,
        TemplatesFromClassicDialogComponent,
        ProjectScoresInfoComponent,
        AboutPanelComponent,
        PortalMenuComponent,
        ApplicationMenuComponent,
        FavoriteMenuComponent,
        StudentInFocusMenuComponent,
        LayoutEditorComponent,
        GetFieldDisplayNamePipe,
        SavedLayoutsDialogComponent,
        CustomLayoutComponent,
        SaveLayoutDialogComponent,
        LayoutUnsavedChangesDialogComponent,
        CustomLayoutSectionComponent,
        CustomLayoutFieldComponent,
        CustomLayoutFieldLabelComponent,
        GetDataTypePipe,
        GetSpecialFieldSectionIdPipe,
        StudentInFocusComponent,
        AddEditStudentListDialogComponent,
        EnterCsvDialogComponent,
        PortalAccessDeniedComponent,
        StudentSearchWarningComponent,
        EFormViewerComponent,
        EFormViewerColumnComponent,
        EFormViewerRowComponent,
        EFormViewerControlComponent,
        AnnouncementTableComponent,
        AnnouncementMessageComponent,
        SelectedRolesDialogComponent,
        SafeResourceUrlPipe,
        ProgramsAndServicesComponent,
        StudentInformationComponent,
        StudentInformationDirective,
        StudentInfoPortalComponent,
        BroadcastDialogComponent,
        MassEditReferralDialogComponent,
        AdminCommentDialogComponent,
        StudentListModalComponent,
        CourseSectionSearchComponent,
        CommaDelimitedPipe,
        TylTranslatePipe,
        MergeFieldPipe,
        TylTranslatePipe,
        EFormNotesComponent,
        AddEditStudentRegCodeDialogComponent,
        StaffSearchComponent,
        DecimalFormatValidatorDirective,
        AddNonStudentDialogComponent,
        SanitizePipe,
        SaveWarningDialogComponent,
        ProgressBarComponent,
        ExternalLinkDirective,
        NumberMaskDirective,
        StudentImageDialogComponent,
        MultipleFamiliesComponent,
        GradebookAccessDeniedComponent,
        DateIsTodayPipe,
        TylCalWidgetComponent,
        EditTranslationsComponent,
        EditRichTextComponent,
        IepComponent,
        GPARankLegendComponent,
        LegendComponent,
        GradeOverrideLegendComponent,
        MinimumDirective,
        MaximumDirective,
        LastFirstNamePipe,
        DebounceDirective,
        TylRTFComponent,
        ReEnrollStudentsComponent,
        NoInternetConnectionModalComponent,
        TylKendoPdfExportComponent,
        AddEditCommunicationLogComponent,
        TranslationsStatusComponent,
        StaffListDialogComponent,
        AdvancedStaffSearchComponent,
        GroupConditionInfoDialogComponent,
        MinimumEnforceDirective,
        MaximumEnforceDirective,
        PhoneNumberPipe,
        MaxPrecisionDirective,
        EFormDesignerComponent,
        EFormColumnComponent,
        EFormRowComponent,
        EFormControlComponent,
        EFormSidePanelComponent,
        EFormNavigationPanelComponent,
        EFormPropertiesPanelComponent,
        EFormAvailableControlsComponent,
        MergeFieldDialogComponent,
        EFormChoicesDialogComponent,
        ValueFromAnotherFieldDialogComponent,
        EFormTemplateInUseWarningComponent,
        GetDesignerControlNamePipe,
        FormatEFormControlTypePipe,
        GetControlImagePipe,
        EFormViewerModalComponent,
        OverrideCreditcodeComponent,
        WarningDialogComponent,
        EFormPdfNameComponent,
        FloatingGridComponent,
        IncidentTypePipe,
        GradebookPreferencesResetWarningsComponent,
        EFormRrowColumnLocationComponent,
        EFormSelectedStudentsComponent,
        CustomDataFieldComponent,
        StudentDocumentAccessHistoryComponent,
        QuillTextOnlyComponent,
        QuillTextOnlyOneLineComponent,
        SelectorComponent,
        TylRaceEthnicityComponent,
        TylRaceEthnicityListComponent,
        GetSurveyAnswerByFieldIDPipe,
        SurveyAnswersPopupComponent,
        SurveyAnswerIsCriticalPipe,
        PreferencesButtonComponent,
        SurveyNotCompletedPopupComponent,
        EFormSurveyAnswerSummaryComponent,
        CalculatePercentPipe,
        ProgramAndServiceFieldsComponent,
        NotificationsTableComponent,
        NotificationDetailsDialogComponent,
        StudentsToAddDialogComponent,
        StudentNotesComponent,
        StudentNotesSharedComponent,
        StudentInfoDirective,
        SortByPipe,
        BehaviorLetterViewerModalComponent,
        EFormSurveyAnswerDetailsComponent,
        EFormSurveyAnswersFilterComponent,
        HouseholdFamilyDocumentComponent,
        AddFamilyDocumentDialogComponent,
        SelectStudentPipe,
        SingleStudentComponent,
        AddEditProgramServiceSingleStudentComponent,
        InvalidSelectionDialogComponent,
        DeleteProgramServiceDialogComponent,
        TylTimeFieldComponent,
        CopyButtonComponent,
        PasteButtonComponent,
        AttendanceCheckInOutLegendComponent,
        SelfCheckInOutHistoryDialogComponent,
        AssignedSchoolsComponent,
        StaffAlertComponent,
        StudentHealthAlertComponent,
        ViewAttachmentsComponent,
        ViewAbsentNoticeHistoryDialogComponent,
        CheckInOutViewHistoryDialogComponent,
        TylDragSelectionComponent,
        YearDescriptionPipe,
        HighlightSearchPipe,
        MassEmailViewComponent,
        SelectRecipientsComponent,
        ViewRecipientsModalComponent,
        UnselectedWarningComponent,
        NoEmailWarningComponent,
        MassEmailAttachmentComponent,
        SendEmailsWarningComponent,
        JobSentToQueueComponent,
        SelectRecipientsPipe,
        ByClassRowStudentComponent,
        SendTestEmailComponent,
        ByTeamRowStudentComponent,
        ByListRowStudentComponent,
        ReplacePipe,
        EFormViewerHeaderComponent,
        EFormHeaderComponent,
        CmpComponentDirective,
        DataDefDropdownComponent,
        DataDefButtonComponent,
        DataDefinitionComponent,
        DataDefinitionStaticComponent,
        DataDefinitionDynamicComponent,
        AddEditDataDefinitionComponent,
        AddEditDynamicDataDefinitionComponent,
        TylKendoHtmlEditorComponent,
        MassEditDialogComponent,
        ValueToUpdateComponent,
        DataDefRadioButtonsComponent,
        DataDefButtonListComponent,
        AssessmentComponent,
        AssessmentDataCategoryTableComponent,
        AssessmentDataTableComponent,
        AddAssessmentScoresComponent,
        AddAssessmentTabDialogComponent,
        TylToggleChipComponent,
        EFormFormatWarningComponent,
        EFormConfirmSubmitComponent,
        AddEditNoteDialogComponent,
        DataDefMultiSelectComponent,
        BehaviorLetterTemplateSettingsComponent,
        MyReportsComponent,
        MyReportsTabComponent,
        AssignedReportsTabComponent,
        AddMyReportFolderDialogComponent,
        FoldersThisReportDialogComponent,
        StaffImageDialogComponent,
        AttachmentTableComponent,
        MonthDayPickerComponent,
        CourseSearchOptionComponent,
        ColorLegendDialogComponent,
    ],
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        MaterialModule,
        TranslateModule,
        CdkTableModule,
        PlatformModule,
        QuillModule.forRoot(),
        MatTableModule,
        ColorPickerModule,
        TextMaskModule,
        OrderModule,
        PDFExportModule,
        DropDownsModule,
        MatChipsModule,
        EditorModule,
        ButtonsModule,
        WindowModule,
        InputsModule,
        FormFieldModule,
        LabelModule,
        NgxMaskModule.forRoot()
    ],
    exports: [
        CommonModule,
        RouterModule,
        FormsModule,
        TylButtonComponent,
        TylHeaderButtonComponent,
        ErrorPromptComponent,
        StudentHeaderComponent,
        StaffHeaderComponent,
        ActionMenuAgGridComponent,
        ActionMenuComponent,
        MaterialModule,
        FormsModule,
        ReactiveFormsModule,
        SaveCancelButtonsComponent,
        TranslateModule,
        Draggable,
        CdkTableModule,
        CdkDetailRowDirective,
        PlatformModule,
        MassEmailComponent,
        SpinnerComponent,
        ExpandCollapseButtonsComponent,
        TabsComponent,
        StudentSelectionComponent,
        GeneralSelectionComponent,
        FilterComponent,
        FilterContentsComponent,
        SafeHtml,
        AriaDirective,
        RegexDirective,
        EllipsisDirective,
        QuillModule,
        StudentNameComponent,
        PageHeaderComponent,
        ColorPickerModule,
        StudentSearchComponent,
        PageHeaderComponent,
        AddAttachmentDialogComponent,
        StudentAlertsComponent,
        AddFileComponent,
        ProfileTableComponent,
        EditTopicDialogComponent,
        DeleteTopicDialogComponent,
        AddEditContentDialogComponent,
        AttachmentsMenuDialogComponent,
        AddProfileAttachmentDialogComponent,
        DeleteProfileAttachmentDialogComponent,
        AddTopicDialogComponent,
        ProfileViewerDialogComponent,
        ChildPortalFilterPipe,
        SchoolLayoutFilterPipe,
        SchoolLayoutAllFilterPipe,
        PortalFilterPipe,
        TileFilterPipe,
        EllipsisPipe,
        PhoneNumberPipe,
        AdditionalTeacherWarningDialogComponent,
        CalculationPreferenceWarningDialogComponent,
        CustomSortWarningDialogComponent,
        NoGradingClassesDialogComponent,
        PrimaryTeacherWarningDialogComponent,
        SyncPreferencesDialogComponent,
        CourseDescriptionDialogComponent,
        DarkTabsComponent,
        NumbersOnlyDirective,
        DateCharactersOnlyDirective,
        TylDatepickerComponent,
        FavoritesComponent,
        TextMaskModule,
        TylMatMenuComponent,
        TylHeaderMatMenuComponent,
        TylFormFieldComponent,
        TylGeneralMatMenuComponent,
        TylCheckboxComponent,
        TylRadioGroupComponent,
        TylCheckboxListComponent,
        TylMultipleSelectListComponent,
        TylSelectCheckAllComponent,
        MenuFilterByGroupPipe,
        DateValidator,
        ScrollToRowDirective,
        VirtualizedTableComponent,
        FilterNotificationComponent,
        LookUpFilterPipe,
        LookUpDescription,
        CustomPageComponent,
        CustomFieldComponent,
        YesNoPipe,
        ClassroomSummaryFilterComponent,
        DataQueryFilterTableComponent,
        DataQueryFieldLabelComponent,
        FieldSearchComponent,
        DataQueryQueriesButtonComponent,
        OrderModule,
        PhoneFormatDirectiveValidator,
        EmailFormatDirectiveValidator,
        FalseyRequiredDirectiveValidator,
        OverlayHeaderComponent,
        PortalMenuComponent,
        ApplicationMenuComponent,
        FavoriteMenuComponent,
        StudentInFocusMenuComponent,
        CustomLayoutComponent,
        EFormViewerComponent,
        SelectedSitesDialogComponent,
        AnnouncementTableComponent,
        SelectedRolesDialogComponent,
        SafeResourceUrlPipe,
        CommaDelimitedPipe,
        TylTranslatePipe,
        MergeFieldPipe,
        TylTranslatePipe,
        DecimalFormatValidatorDirective,
        ExternalLinkDirective,
        NumberMaskDirective,
        MultipleFamiliesComponent,
        TylCalWidgetComponent,
        DateIsTodayPipe,
        EditTranslationsComponent,
        EditRichTextComponent,
        MinimumDirective,
        MaximumDirective,
        LastFirstNamePipe,
        DebounceDirective,
        TylRTFComponent,
        PDFExportModule,
        DropDownsModule,
        InputsModule,
        EditorModule,
        ButtonsModule,
        WindowModule,
        LabelModule,
        FormFieldModule,
        TylKendoPdfExportComponent,
        TranslationsStatusComponent,
        AddEditCommunicationLogComponent,
        MinimumEnforceDirective,
        MaximumEnforceDirective,
        MaxPrecisionDirective,
        EFormDesignerComponent,
        MergeFieldDialogComponent,
        EFormChoicesDialogComponent,
        ValueFromAnotherFieldDialogComponent,
        EFormTemplateInUseWarningComponent,
        EFormViewerModalComponent,
        FloatingGridComponent,
        IncidentTypePipe,
        EFormSelectedStudentsComponent,
        CustomDataFieldComponent,
        StudentDocumentAccessHistoryComponent,
        QuillTextOnlyComponent,
        QuillTextOnlyOneLineComponent,
        SelectorComponent,
        TylRaceEthnicityComponent,
        SurveyAnswersPopupComponent,
        GetSurveyAnswerByFieldIDPipe,
        SurveyAnswerIsCriticalPipe,
        PreferencesButtonComponent,
        SurveyNotCompletedPopupComponent,
        EFormSurveyAnswerSummaryComponent,
        CalculatePercentPipe,
        ProgramAndServiceFieldsComponent,
        NotificationsTableComponent,
        StudentInfoDirective,
        SortByPipe,
        BehaviorLetterViewerModalComponent,
        MatChipsModule,
        HouseholdFamilyDocumentComponent,
        AddFamilyDocumentDialogComponent,
        SelectStudentPipe,
        SingleStudentComponent,
        AddEditProgramServiceSingleStudentComponent,
        InvalidSelectionDialogComponent,
        DeleteProgramServiceDialogComponent,
        TylTimeFieldComponent,
        CopyButtonComponent,
        PasteButtonComponent,
        AttendanceCheckInOutLegendComponent,
        SelfCheckInOutHistoryDialogComponent,
        AssignedSchoolsComponent,
        StaffAlertComponent,
        StudentHealthAlertComponent,
        ViewAttachmentsComponent,
        ViewAbsentNoticeHistoryDialogComponent,
        CheckInOutViewHistoryDialogComponent,
        TylDragSelectionComponent,
        YearDescriptionPipe,
        HighlightSearchPipe,
        ReplacePipe,
        CmpComponentDirective,
        DataDefDropdownComponent,
        DataDefButtonComponent,
        DataDefinitionComponent,
        DataDefinitionStaticComponent,
        DataDefinitionDynamicComponent,
        AddEditDataDefinitionComponent,
        AddEditDynamicDataDefinitionComponent,
        TylKendoHtmlEditorComponent,
        MassEditDialogComponent,
        ValueToUpdateComponent,
        DataDefRadioButtonsComponent,
        DataDefButtonListComponent,
        AssessmentComponent,
        TylToggleChipComponent,
        EFormFormatWarningComponent,
        EFormConfirmSubmitComponent,
        DataDefMultiSelectComponent,
        BehaviorLetterTemplateSettingsComponent,
        MyReportsComponent,
        MyReportsTabComponent,
        AssignedReportsTabComponent,
        AddMyReportFolderDialogComponent,
        FoldersThisReportDialogComponent,
        MonthDayPickerComponent,
        NgxMaskModule,
        CourseSearchOptionComponent
    ],
    providers: [
        MergeFieldPipe,
        BehaviorService,
        GetDesignerControlNamePipe,
        TylTranslatePipe,
        GetSurveyAnswerByFieldIDPipe,
        SurveyAnswerIsCriticalPipe,
        CalculatePercentPipe,
        StudentNotesService,
        SelectStudentPipe,
        RegistrationEnrollmentService,
        ReportingFrameworkService,
        ReplacePipe,
        RequireStudentGuard,
        RequireStaffGuard,
        { provide: MAT_TABS_CONFIG, useValue: { animationDuration: '0ms' } }
    ]
})
export class SharedComponentsModule {
    static forRoot(): ModuleWithProviders<SharedComponentsModule> {
        return {
            ngModule: SharedComponentsModule,
            providers: [
                LookupService,
                StudentSearchService,
                FamilyRegistrationService,
                OnlineRegistrationService,
                ProfileService,
                BehaviorSharedService,
                SpinnerService,
                CustomLayoutsService,
                CustomPageService,
                DisciplineReferralService,
                FilterClass,
                DataQueryService,
                SharedGradebookService,
                TranslationService,
                EFormService,
                StudentListsService,
                SendEmailService,
                ProgramsAndServicesService,
                AnnouncementService,
                StaffSearchService,
                CourseSectionSearchService,
                EFormDesignerService,
                CustomizationFrameworkService,
                CustomizationService,
                StaffCardsService,
                NotificationsService,
                CourseRequestMgmtService,
                ScheduleFolderService,
                MassEmailService
            ]
        };
    }
}
